import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useRef,
} from "react";

interface NavigationGuardContextType {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  setNavigationHandler: (
    handler: ((callback: () => void) => void) | null,
  ) => void;
  requestNavigation: (callback: () => void) => void;
}

// デフォルト値を設定（Providerがない場合は何もしない）
const defaultContext: NavigationGuardContextType = {
  hasUnsavedChanges: false,
  setHasUnsavedChanges: () => {},
  setNavigationHandler: () => {},
  requestNavigation: (callback) => callback(), // デフォルトでは即座に実行
};

const NavigationGuardContext =
  createContext<NavigationGuardContextType>(defaultContext);

export const useNavigationGuard = () => {
  return useContext(NavigationGuardContext);
};

interface NavigationGuardProviderProps {
  children: React.ReactNode;
}

export const NavigationGuardProvider: React.FC<
  NavigationGuardProviderProps
> = ({ children }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const navigationHandlerRef = useRef<((callback: () => void) => void) | null>(
    null,
  );

  const setNavigationHandler = useCallback(
    (handler: ((callback: () => void) => void) | null) => {
      navigationHandlerRef.current = handler;
    },
    [],
  );

  const requestNavigation = useCallback(
    (callback: () => void) => {
      if (hasUnsavedChanges && navigationHandlerRef.current) {
        navigationHandlerRef.current(callback);
      } else {
        callback();
      }
    },
    [hasUnsavedChanges],
  );

  const value = useMemo(
    () => ({
      hasUnsavedChanges,
      setHasUnsavedChanges,
      setNavigationHandler,
      requestNavigation,
    }),
    [hasUnsavedChanges, setNavigationHandler, requestNavigation],
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
    </NavigationGuardContext.Provider>
  );
};
